"use client"

import { useCallback, useEffect, useState, useMemo, useRef } from "react"
import { Box, MenuItem, TextField } from "@mui/material"
import ExitToAppIcon from "@mui/icons-material/ExitToApp"
import ViewHeadlineIcon from "@mui/icons-material/ViewHeadline"
import CakeIcon from "@mui/icons-material/Cake"
import axios from "axios"
import useAuthStore from "../../../Zustand/Store/useAuthStore"
import usePermissionDataStore from "../../../Zustand/Store/usePermissionDataStore"
import NotAllowed from "../../../Exceptions/NotAllowed"
import toast from "react-hot-toast"
import PeopleIcon from "@mui/icons-material/People"
import { MAIN_URL } from "../../../Configurations/Urls"
import { useNavigate, useLocation, useParams, useSearchParams } from "react-router-dom"
import TableDataGeneric from "../../../Configurations/TableDataGeneric"
import Layout4 from "../../DataLayouts/Layout4"
import EmployeeOrganizationalSetupDialog from "./EmployeeOrganizationalSetupDialog"
import { operatorRequiresValue } from '../../../Components/filters/operators';
import {
  EMPLOYEE_ADD_ROUTE,
  fetchEmployeeSetupStatus,
} from "../../../Utils/employeeSetupConfig";
import {
  EMPLOYEES_DATAGRID_KEY,
  mapSavedEmployeeColumns,
} from "./employeeListConstants";

/** Resolve organization_person_id for delete/edit URLs (table may pass row object or raw id). */
const resolveOrganizationPersonId = (itemOrId) => {
  if (itemOrId == null || itemOrId === "") return null;
  if (typeof itemOrId === "object") {
    const raw =
      itemOrId.organization_person_id ??
      itemOrId.id ??
      itemOrId.employee_id ??
      null;
    if (raw == null || raw === "") return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : raw;
  }
  const n = Number(itemOrId);
  return Number.isFinite(n) ? n : itemOrId;
};

// Column fields must match backend response keys (department_name, designation_name, etc.)
const DEFAULT_COLUMNS = [
  {
    field: "employee_code",
    label: "Emp Code",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "name",
    label: "Name",
    visible: true,
    width: 200,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "department_name",
    label: "Department",
    visible: true,
    width: 180,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "designation_name",
    label: "Designation",
    visible: true,
    width: 180,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "work_shift_name",
    label: "Work Shift",
    visible: true,
    width: 200,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
]

const buildEmployeeDisplayName = (emp) => {
  if (emp?.name && String(emp.name).trim()) return emp.name
  if (emp?.full_name && String(emp.full_name).trim()) return emp.full_name
  return [emp?.first_name, emp?.middle_name, emp?.last_name]
    .filter(Boolean)
    .join(" ")
    .trim()
}

// Backward compatibility: map legacy/saved column keys to canonical keys sent to API
const SORT_FIELD_ALIASES = {
  department: "department_name",
  designation: "designation_name",
  location: "location_name",
  work_shift: "work_shift_name",
}

function EmployeeList({ mode }) {
  const { userData } = useAuthStore()
  const { Permission } = usePermissionDataStore()
  const org = userData?.organization
  const [showNotAllowed, setShowNotAllowed] = useState(false)

  const hasEditPermission = useMemo(
    () =>
      Permission &&
      Array.isArray(Permission) &&
      Permission.includes("EMPLOYEE_EDIT"),
    [Permission],
  )

  const hasDeletePermission = useMemo(
    () =>
      Permission &&
      Array.isArray(Permission) &&
      Permission.includes("EMPLOYEE_DELETE"),
    [Permission],
  )

  const [employee, setemployee] = useState([])
  const [loading, setLoading] = useState(true)
  const location = useLocation()
  const { id } = useParams()
  const fromDashboard = location.state?.fromDashboard || false
  const [searchParams, setSearchParams] = useSearchParams()

  const [paginationData, setPaginationData] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 1,
    hasNext: false,
    hasPrev: false,
  })

  const [tableConfig, setTableConfig] = useState(null)
  const [configColumns, setConfigColumns] = useState(DEFAULT_COLUMNS)
  const [loadingConfig, setLoadingConfig] = useState(true)
  const [setupDialogOpen, setSetupDialogOpen] = useState(false)
  const [missingSetup, setMissingSetup] = useState({})
  const [checkingSetup, setCheckingSetup] = useState(false)
  const [departmentOptions, setDepartmentOptions] = useState([])
  const [designationOptions, setDesignationOptions] = useState([])

  const fetchControllerRef = useRef(null)
  const hasInitializedURL = useRef(false)
 

  // Initialize URL parameters with defaults on mount
  useEffect(() => {
    if (hasInitializedURL.current) return

    const currentParams = new URLSearchParams(searchParams)
    let needsUpdate = false

    if (!currentParams.has("page")) {
      currentParams.set("page", "1")
      needsUpdate = true
    }

    if (!currentParams.has("pageSize")) {
      currentParams.set("pageSize", "10")
      needsUpdate = true
    }

    if (!currentParams.has("sortBy")) {
      currentParams.set("sortBy", "employee_code")
      needsUpdate = true
    }

    if (!currentParams.has("sortOrder")) {
      currentParams.set("sortOrder", "desc")
      needsUpdate = true
    }

    if (needsUpdate) {
      setSearchParams(currentParams, { replace: true })
    }

    hasInitializedURL.current = true
  }, [])

  // // Build query params from URL
  // const buildQueryParamsFromURL = useMemo(() => {
  //   const params = {}
  //   params.page = Number.parseInt(searchParams.get("page"), 10) || 1
  //   params.pageSize = Number.parseInt(searchParams.get("pageSize"), 10) || 10

  //   const search = searchParams.get("search")
  //   if (search) params.search = search

  //   const sortBy = searchParams.get("sortBy")
  //   const sortOrder = searchParams.get("sortOrder")
  //   if (sortBy) {
  //     params.sortBy = sortBy
  //     params.sortOrder = sortOrder || "asc"
  //   }

  //   searchParams.forEach((value, key) => {
  //     if (key.startsWith("filter_") && value) {
  //       const filterKey = key.replace("filter_", "")
  //       params[filterKey] = value
  //     }
  //   })

  //   return params
  // }, [searchParams])

  // Fetch employees with filters - FIXED
  const fetchEmployeesWithFilters = useCallback(async () => {
    if (!org?.organization_id) return

    if (fetchControllerRef.current) {
      fetchControllerRef.current.abort()
    }

    fetchControllerRef.current = new AbortController()

    setLoading(true)
    try {
      // Build filters array from URL params
      const filters = []
      const processedFields = new Set() // Track processed fields to avoid duplicates
      let filterIndex = 0 // Track filter index
     
      searchParams.forEach((value, key) => {
        // Skip operator and logic params - they'll be processed with their field
        if (key.endsWith("_operator") || key.endsWith("_logic")) return
       
        if (key.startsWith("filter_")) {
          const field = key.replace("filter_", "")
         
          // Skip if already processed
          if (processedFields.has(field)) return
          processedFields.add(field)
         
          // Get operator from URL (e.g., filter_employee_code_operator=contains)
          const operatorKey = `filter_${field}_operator`
          const operator = searchParams.get(operatorKey) || 'contains' // Default to contains
         
          // Check if operator requires a value
          const requiresValue = operatorRequiresValue(operator)
         
          // Include filter if:
          // 1. Operator doesn't require value (today, this_week, this_month, is_null, is_not_null)
          // 2. OR value exists
          if (!requiresValue || value) {
            // For operators that don't require values, set value to null
            let filterValue = requiresValue ? value : null
           
            // Convert comma-separated string to array for operators that need arrays
            if (requiresValue && filterValue) {
              if (operator === 'equals' || operator === 'not_equals') {
                // Split by comma and trim whitespace, then convert to lowercase
                filterValue = filterValue.split(',').map(v => v.trim().toLowerCase())
              } else if (operator === 'between' || operator === 'not_between') {
                // Split by comma and trim whitespace for range operators
                filterValue = filterValue.split(',').map(v => v.trim())
              }
            }
           
            filters.push({
              field: field,
              operator: operator,
              value: filterValue
            })
           
            filterIndex++ // Increment index for next filter
          }
        }
      })

      // Global search: send as 'search' so backend metadata engine searches all filterable fields
      const search = searchParams.get("search")

      const departmentId = String(searchParams.get("organization_department_id") || "").trim()
      const designationId = String(searchParams.get("organization_designation_id") || "").trim()

      const requestBody = {
        filters: filters,
        ...(departmentId && departmentId.toLowerCase() !== "all"
          ? { organization_department_id: departmentId }
          : {}),
        ...(designationId && designationId.toLowerCase() !== "all"
          ? { organization_designation_id: designationId }
          : {}),
        page: Number.parseInt(searchParams.get("page"), 10) || 1,
        pageSize: Number.parseInt(searchParams.get("pageSize"), 10) || 10,
        sort: {
          field: searchParams.get("sortBy") || "employee_code",
          direction: searchParams.get("sortOrder") || "desc"
        },
        ...(search && search.trim() !== "" && { search: search.trim() })
      }

      const response = await fetch(
        `${MAIN_URL}/api/organizations/${org.organization_id}/employee/filters`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
          signal: fetchControllerRef.current.signal,
        },
      )

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const result = await response.json()
      
      // Debug logging
      console.log('Employee list API response:', result)
      console.log('Success:', result.success)
      console.log('Data length:', result.data?.length)
      console.log('Error:', result.error)
      console.log('Message:', result.message)

      if (result.success && result.data) {
        // Normalize profile_image_url to always be a full, usable URL for card view
        const normalizedEmployees = result.data.map((emp) => {
          const displayName = buildEmployeeDisplayName(emp)
          const organizationPersonId =
            emp?.organization_person_id ?? emp?.id ?? emp?.employee_id ?? ""
          const withPersonId = {
            ...emp,
            organization_person_id: organizationPersonId,
          }
          const raw = emp?.profile_image_url

          if (!raw || typeof raw !== "string") {
            return {
              ...withPersonId,
              name: displayName,
              profile_image_url: "",
            }
          }

          // If backend already sends full URL (http/https), keep as-is
          if (raw.startsWith("http://") || raw.startsWith("https://")) {
            return { ...withPersonId, name: displayName, profile_image_url: raw }
          }

          // If raw already includes its folder (employees/, persons/, interns/),
          // prepend the storage base only once
          if (
            raw.startsWith("employees/") ||
            raw.startsWith("persons/") ||
            raw.startsWith("interns/")
          ) {
            return {
              ...withPersonId,
              name: displayName,
              profile_image_url: `${MAIN_URL}/storage/app/public/${raw}`,
            }
          }

          // Backward compat: treat as a plain filename in employees folder
          return {
            ...withPersonId,
            name: displayName,
            profile_image_url: `${MAIN_URL}/storage/app/public/employees/${raw}`,
          }
        })

        setemployee(normalizedEmployees)

        if (result.pagination) {
          // Backend may return 'limit' instead of 'pageSize'
          const pageSize = result.pagination.pageSize || result.pagination.limit || 10;
          const requestedPage = Number.parseInt(searchParams.get("page"), 10) || 1;
         
          setPaginationData({
            page: result.pagination.page || requestedPage, // Use requested page if backend doesn't return it
            pageSize: pageSize,
            total: result.pagination.total || 0,
            totalPages: result.pagination.totalPages || 1,
            hasNext: result.pagination.hasNext || false,
            hasPrev: result.pagination.hasPrev || false,
          })
        }
      } else {
        console.warn('API returned unsuccessful response:', result)
        setemployee([])
        setPaginationData({
          page: 1,
          pageSize: 10,
          total: 0,
          totalPages: 1,
          hasNext: false,
          hasPrev: false,
        })
        // Show error message to user
        if (result.message || result.error) {
          toast.error(result.message || result.error || 'Failed to fetch employees')
        }
      }
    } catch (err) {
      if (err.name === "AbortError") {
        console.log("Fetch aborted")
        return
      }
      console.error("Error fetching employees:", err)
      toast.error("Failed to fetch employees")
      setemployee([])
      setPaginationData({
        page: 1,
        pageSize: 10,
        total: 0,
        totalPages: 1,
        hasNext: false,
        hasPrev: false,
      })
    } finally {
      setLoading(false)
    }
  }, [org?.organization_id, searchParams])

  useEffect(() => {
    if (!org?.organization_id) {
      setDepartmentOptions([])
      setDesignationOptions([])
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        const headers = {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        }
        const [departmentResponse, designationResponse] = await Promise.all([
          axios.get(`${MAIN_URL}/api/organizations/${org.organization_id}/department-all`, { headers }),
          axios.get(`${MAIN_URL}/api/organizations/${org.organization_id}/designation-all`, { headers }),
        ])
        if (cancelled) return
        const departments = (Array.isArray(departmentResponse.data)
          ? departmentResponse.data
          : departmentResponse.data?.data || []
        )
          .map((item) => ({
            id: item?.organization_department_id ?? item?.id,
            name: String(item?.department_name || "").trim(),
          }))
          .filter((item) => item.id != null && item.name)
          .sort((a, b) => a.name.localeCompare(b.name))
        const designations = (Array.isArray(designationResponse.data)
          ? designationResponse.data
          : designationResponse.data?.data || []
        )
          .map((item) => ({
            id: item?.organization_designation_id ?? item?.id,
            name: String(item?.designation_name || "").trim(),
          }))
          .filter((item) => item.id != null && item.name)
          .sort((a, b) => a.name.localeCompare(b.name))
        setDepartmentOptions(departments)
        setDesignationOptions(designations)
      } catch (err) {
        console.error("Error fetching employee department filters:", err)
        if (!cancelled) {
          setDepartmentOptions([])
          setDesignationOptions([])
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [org?.organization_id])

  const selectedDepartment = (() => {
    const raw = String(searchParams.get("organization_department_id") || "").trim()
    return !raw || raw.toLowerCase() === "all" ? "All" : raw
  })()
  const selectedDesignation = (() => {
    const raw = String(searchParams.get("organization_designation_id") || "").trim()
    return !raw || raw.toLowerCase() === "all" ? "All" : raw
  })()
  const handleDepartmentFilterChange = (event) => {
    const value = String(event.target.value || "").trim()
    const next = new URLSearchParams(searchParams)
    if (value && value !== "All") next.set("organization_department_id", value)
    else next.delete("organization_department_id")
    next.set("page", "1")
    setSearchParams(next)
  }
  const handleDesignationFilterChange = (event) => {
    const value = String(event.target.value || "").trim()
    const next = new URLSearchParams(searchParams)
    if (value && value !== "All") next.set("organization_designation_id", value)
    else next.delete("organization_designation_id")
    next.set("page", "1")
    setSearchParams(next)
  }
  const employeeFilterToolbar = (
    <Box sx={{ display: "flex", gap: 1.5, flexShrink: 0 }}>
      <TextField
        select
        size="small"
        label="Department"
        value={selectedDepartment}
        onChange={handleDepartmentFilterChange}
        sx={{
          width: 240,
          minWidth: 240,
          "& .MuiSelect-select": {
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          },
        }}
      >
        <MenuItem value="All">All</MenuItem>
        {departmentOptions.map((department) => (
          <MenuItem key={String(department.id)} value={String(department.id)}>
            {department.name}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        select
        size="small"
        label="Designation"
        value={selectedDesignation}
        onChange={handleDesignationFilterChange}
        sx={{
          width: 240,
          minWidth: 240,
          "& .MuiSelect-select": {
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          },
        }}
      >
        <MenuItem value="All">All</MenuItem>
        {designationOptions.map((designation) => (
          <MenuItem key={String(designation.id)} value={String(designation.id)}>
            {designation.name}
          </MenuItem>
        ))}
      </TextField>
    </Box>
  )

  // Load personalized layout (same API/key as Personalize View)
  useEffect(() => {
    const loadTableConfiguration = async () => {
      if (!org?.organization_id) {
        setLoadingConfig(false)
        return
      }

      try {
        const datagridKey = `${EMPLOYEES_DATAGRID_KEY}_${org.organization_id}`
        const configRes = await fetch(`${MAIN_URL}/api/datagrid/get-by-context`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({
            datagrid_key: datagridKey,
            organization_id: org.organization_id,
          }),
        })

        if (configRes.ok) {
          const configResponse = await configRes.json()
          const serverCfg =
            configResponse?.datagrid?.datagrid_default_configuration

          if (serverCfg?.columns?.length > 0) {
            setTableConfig(serverCfg)
            setConfigColumns(mapSavedEmployeeColumns(serverCfg.columns))
          } else {
            setTableConfig(null)
            setConfigColumns(DEFAULT_COLUMNS)
          }
        } else {
          setTableConfig(null)
          setConfigColumns(DEFAULT_COLUMNS)
        }
      } catch (error) {
        console.error("Error loading table configuration:", error)
        setTableConfig(null)
        setConfigColumns(DEFAULT_COLUMNS)
      } finally {
        setLoadingConfig(false)
      }
    }

    loadTableConfiguration()
  }, [org?.organization_id])

  // Fetch employees when URL params change
  useEffect(() => {
    if (org?.organization_id && !loadingConfig && hasInitializedURL.current) {
      fetchEmployeesWithFilters()
    }

    return () => {
      if (fetchControllerRef.current) {
        fetchControllerRef.current.abort()
      }
    }
  }, [org?.organization_id, loadingConfig, searchParams, fetchEmployeesWithFilters])

  const deleteemployee = useCallback(
    async (itemOrId) => {
      const personId = resolveOrganizationPersonId(itemOrId);
      if (!personId) {
        toast.error("Unable to delete: employee ID is missing");
        return;
      }
      try {
        const org_id = org.organization_id
        await axios.delete(
          `${MAIN_URL}/api/organizations/${org_id}/employee/${personId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        )
        toast.success("Employee deleted successfully")
        fetchEmployeesWithFilters()
      } catch (error) {
        if (error.response && error.response.status === 401) {
          window.location.href = "/login"
          toast.error("Session Expired!")
        } else {
          const msg =
            error.response?.data?.errors?.organization_person_id?.[0] ||
            error.response?.data?.message ||
            "Failed to delete employee";
          toast.error(msg)
        }
      }
    },
    [org, fetchEmployeesWithFilters],
  )

  const navigate = useNavigate()

  // Handle sort change - update URL parameters; map legacy column keys for backward compatibility
  const handleSortChange = useCallback(
    (sortField, sortDirection) => {
      const raw = (sortField && String(sortField).trim()) || ""
      const normalizedField = raw.toLowerCase()
      const canonicalField = SORT_FIELD_ALIASES[normalizedField] ?? (normalizedField || raw)

      const params = new URLSearchParams(searchParams)
      params.set("sortBy", canonicalField)
      params.set("sortOrder", sortDirection)
      params.set("page", "1") // Reset to first page on sort
      setSearchParams(params, { replace: true })
    },
    [searchParams, setSearchParams],
  )

  const handleDelete = useCallback(
    async (itemOrId) => {
      if (!hasDeletePermission) {
        setShowNotAllowed(true)
        return Promise.reject(new Error("Not allowed"))
      }
      const personId = resolveOrganizationPersonId(itemOrId);
      if (!personId) {
        toast.error("Unable to delete: employee ID is missing");
        return Promise.reject(new Error("Missing organization_person_id"))
      }
      try {
        const response = await fetch(
          `${MAIN_URL}/api/organizations/${org?.organization_id}/employee/${personId}`,
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
          }
        )

        if (!response.ok) {
          const data = await response.json().catch(() => ({}))
          const msg =
            data?.errors?.organization_person_id?.[0] ||
            data?.message ||
            `HTTP error! status: ${response.status}`
          throw new Error(msg)
        }

        toast.success("Employee deleted successfully")
        await fetchEmployeesWithFilters()

        return Promise.resolve()
      } catch (error) {
        toast.error(error.message || "Failed to delete employee")
        return Promise.reject(error)
      }
    },
    [org?.organization_id, hasDeletePermission, fetchEmployeesWithFilters],
  )

  const handleEdit = useCallback(
    (item) => {
      const employeeId = item?.organization_person_id || item?.employee_id || item?.id;
      if (!employeeId) {
        console.error('Employee ID is missing:', item);
        toast.error('Unable to edit: Employee ID is missing');
        return;
      }
      navigate(`/organization/employee/employee-details/edit/${employeeId}`)
    },
    [navigate],
  )

  const carddata = useMemo(
    () => [
      { key: "profile_image_url", type: "photo" },
      { key: "employee_code", type: "String" },
      { key: "name", type: "String" },
      { key: "department_name", type: "String" },
      { key: "designation_name", type: "String" },
      { key: "work_shift_name", type: "String" },
    ],
    [],
  )

  const icons = useMemo(
    () => [
      {
        key: "exitToAppIcon",
        element: <ExitToAppIcon sx={{ fontSize: 60, color: "grey.500", mb: 2 }} />,
      },
      {
        key: "viewHeadlineIcon",
        element: <ViewHeadlineIcon color="primary" />,
      },
      {
        key: "cakeIcon",
        element: <CakeIcon sx={{ color: "red" }} />,
      },
      {
        key: "peopleIcon",
        element: <PeopleIcon sx={{ color: "primary" }} />,
      },
    ],
    [],
  )

  const messages = useMemo(
    () => [
      "Employee details",
      "Employee details",
      "Add Employee",
      "Employee, Deleting This Employee will delete its Entire data from Employees Record of this Organization Permanently",
    ],
    [],
  )


       const handleShow = useCallback(
    (item) => {
      navigate(`/organization/employee/employee-details/view/${item.organization_person_id}`)
    },
    [navigate],
  )

  const handleAddEmployeeClick = useCallback(async () => {
    if (!org?.organization_id) {
      toast.error("Organization not found")
      return
    }

    setCheckingSetup(true)
    try {
      const { ready, missing } = await fetchEmployeeSetupStatus(
        org.organization_id
      )

      if (ready) {
        navigate(EMPLOYEE_ADD_ROUTE)
        return
      }

      setMissingSetup(missing)
      setSetupDialogOpen(true)
    } catch (error) {
      console.error("Failed to verify organization setup:", error)
      toast.error("Failed to verify organization setup. Please try again.")
    } finally {
      setCheckingSetup(false)
    }
  }, [org?.organization_id, navigate])


  if (showNotAllowed) {
    return <NotAllowed />
  }

  return (
    <>
      <Layout4
        loading={loading}
        add_action={"EMPLOYEE_ADD"}
        heading={
          <div style={{ cursor: "pointer" }} onClick={() => navigate(-1)}>
            Employees
          </div>
        }
        btnName={"Add Employee"}
        onAddBtClick={handleAddEmployeeClick}
        addButtonLoading={checkingSetup}
        onEditBtClick={() => null}
        Data={employee}
        Icons={icons}
        messages={messages}
        Route={"/organization/employee/employee-details"}
        setData={setemployee}
        DeleteFunc={deleteemployee}
      />
      <div className="ems-section-divider" />
      {fromDashboard ? (
        <TableDataGeneric
          tableName="Employees"
          primaryKey="organization_person_id"
          heading="Employees"
          data={employee}
          sortname={"name"}
          showActions={false}
          CardData={carddata}
          Route="/organization/employee/employee-details"
          DeleteFunc={hasDeletePermission ? handleDelete : undefined}
          EditFunc={hasEditPermission ? handleEdit : undefined}
          handleShow={handleShow}
          token={localStorage.getItem("token")}
          configss={configColumns}
          paginationData={paginationData}
          onSortChange={handleSortChange}
          filterModule="employees"
          loadedBackendConfig={tableConfig}
          edit_delete_action={["EMPLOYEE_EDIT", "EMPLOYEE_DELETE"]}
          loading={loading || loadingConfig}
          toolbarCenterContent={employeeFilterToolbar}
          {...(tableConfig && { config: tableConfig })}
        />
      ) : (
        <TableDataGeneric
          tableName="Employees"
          primaryKey={"organization_person_id"}
          heading="Employees"
          data={employee}
          sortname={"name"}
          showActions={true}
          CardData={carddata}
          Route="/organization/employee/employee-details"
          DeleteFunc={hasDeletePermission ? handleDelete : undefined}
          EditFunc={hasEditPermission ? handleEdit : undefined}
          handleShow={handleShow}
          token={localStorage.getItem("token")}
          configss={configColumns}
          paginationData={paginationData}
          onSortChange={handleSortChange}
          filterModule="employees"
          loadedBackendConfig={tableConfig}
          edit_delete_action={["EMPLOYEE_EDIT", "EMPLOYEE_DELETE"]}
          loading={loading || loadingConfig}
          toolbarCenterContent={employeeFilterToolbar}
          {...(tableConfig && { config: tableConfig })}
        />
      )}
      <EmployeeOrganizationalSetupDialog
        open={setupDialogOpen}
        onClose={() => setSetupDialogOpen(false)}
        missing={missingSetup}
      />
    </>
  )
}

export default EmployeeList
